import { Ban, Hourglass, Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { ApiRequestError, useApi } from "../api/client";
import { qk, useInvalidateOrg, useOrgs, useSession } from "../api/queries";
import type { Organization } from "../api/types";
import { useQueryClient } from "@tanstack/react-query";
import { common } from "../i18n/common";
import { LOCALE_NAMES, LOCALES, useLocale, useT, type Locale } from "../i18n/core";
import { useFormat } from "../lib/format";
import { ErrorState, LoadingRows, PageHead } from "../ui/Common";
import { Dialog } from "../ui/Dialog";
import { Meter } from "../ui/Meter";
import { Pill } from "../ui/Pills";
import { useToast } from "../ui/Toasts";
import { organizationsMessages } from "./organizations.i18n";

const GB = 1_000_000_000;
const DAY = 86_400_000;

/** Data local (AAAA-MM-DD) de um instante, para o campo de data. */
function dateInput(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Fim do dia escolhido, no fuso de quem está na console, como instante ISO. */
function endOfDayISO(date: string): string {
  return new Date(`${date}T23:59:59`).toISOString();
}

export function OrganizationsPage() {
  const orgs = useOrgs();
  const session = useSession();
  const navigate = useNavigate();
  const t = useT(organizationsMessages);
  const f = useFormat();
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const invalidate = useInvalidateOrg();
  // Cadastro de organização é do administrador da plataforma, como os pools
  // (ADR 0021); os demais só enxergam a lista.
  const canManage = session.data?.user.platformAdmin === true;
  const [editing, setEditing] = useState<Organization | "new" | null>(null);
  const [closing, setClosing] = useState<Organization | null>(null);
  const [purging, setPurging] = useState<Organization | null>(null);
  const customers = orgs.data?.filter((o) => o.kind === "customer") ?? [];
  const msps = orgs.data?.filter((o) => o.kind === "msp") ?? [];
  const active = customers.filter((o) => o.status !== "closed");
  // Um "agora" por renderização, tirado da hora em que a lista chegou.
  const now = orgs.dataUpdatedAt || 0;
  const totalProt = active.reduce((a, o) => a + o.protectedBytes, 0);

  return (
    <>
      <PageHead
        title={t.title}
        lede={orgs.data ? t.lede(active.length, active.reduce((a, o) => a + o.machineCount, 0), f.gb(totalProt)) : undefined}
        actions={canManage ? (
          <button className="btn btn-primary" type="button" onClick={() => setEditing("new")}>
            <Plus aria-hidden="true" /> {t.newOrg}
          </button>
        ) : undefined}
      />
      <section className="panel">
        {orgs.isPending ? <LoadingRows /> : orgs.isError ? <div className="panel-body"><ErrorState error={orgs.error} /></div> : (
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>{t.colCustomer}</th><th>{t.colStatus}</th><th className="right">{t.colMachines}</th><th style={{ width: "32%" }}>{t.colQuota}</th><th><span className="sr-only">{t.open}</span></th></tr></thead>
              <tbody>
                {customers.map((o) => {
                  const href = `/o/${o.id}/overview`;
                  const closed = o.status === "closed";
                  // Encerrada (ADR 0024): só o administrador da plataforma a vê, e
                  // o que se faz com ela é reabrir ou apagar; não se navega.
                  return (
                    <tr key={o.id} className={closed ? undefined : "clickable"} onClick={closed ? undefined : () => navigate(href)}>
                      <td>
                        {closed ? <strong className="muted">{o.name}</strong> : <Link to={href} onClick={(e) => e.stopPropagation()}><strong>{o.name}</strong></Link>}
                        {!closed && o.trialEndsAt && (
                          <span style={{ marginLeft: 8 }}>
                            {+new Date(o.trialEndsAt) <= now
                              ? <Pill tone="crit" icon={Hourglass}>{t.trialEnded}</Pill>
                              : <Pill tone="info" icon={Hourglass}>{t.trialUntil(f.fullDate(o.trialEndsAt))}</Pill>}
                          </span>
                        )}
                        {closed && o.purgeAfter && (
                          <div className="muted" style={{ fontSize: "var(--fs-12)" }}>
                            {o.purgeError ? t.purgeStuck(o.purgeError) : +new Date(o.purgeAfter) <= now ? t.purgeNow : t.purgeOn(f.ago(o.purgeAfter, now))}
                          </div>
                        )}
                      </td>
                      <td>{closed ? <Pill tone="idle" icon={Ban}>{t.statusClosed}</Pill> : o.status === "ok" ? <Pill tone="ok">{t.statusOk}</Pill> : o.status === "attention" ? <Pill tone="warn">{t.statusAttention}</Pill> : <Pill tone="crit">{t.statusCritical}</Pill>}</td>
                      <td className="right num">{f.int(o.machineCount)}</td>
                      <td>
                        {closed ? <span className="muted">—</span>
                          : o.quotaBytes === null ? <span className="muted">{t.storedNoLimit(f.gb(o.storedBytes))}</span>
                          : o.quotaBytes === 0 ? <span className="muted">{t.noStorage}</span>
                          : <Meter value={o.storedBytes} max={o.quotaBytes} label={t.quotaUsed(f.gb(o.storedBytes), f.gb(o.quotaBytes))} detail={f.pct(o.storedBytes / o.quotaBytes)} />}
                      </td>
                      <td className="right" style={{ whiteSpace: "nowrap" }}>
                        {canManage && closed ? (
                          <>
                            <button className="btn btn-sm" type="button" style={{ marginRight: 6 }} onClick={() => setPurging(o)} aria-label={`${t.purge}: ${o.name}`}>
                              <Trash2 aria-hidden="true" /> {t.purge}
                            </button>
                            <button className="btn btn-sm" type="button" onClick={() => void reopen(o)} aria-label={`${t.reopen}: ${o.name}`}>
                              <RotateCcw aria-hidden="true" /> {t.reopen}
                            </button>
                          </>
                        ) : canManage ? (
                          <>
                            <button className="btn btn-sm" type="button" style={{ marginRight: 6 }}
                              onClick={(e) => { e.stopPropagation(); setEditing(o); }} aria-label={`${t.editOrg}: ${o.name}`}>
                              <Pencil aria-hidden="true" /> {t.editOrg}
                            </button>
                            <button className="btn btn-sm" type="button" style={{ marginRight: 6 }}
                              onClick={(e) => { e.stopPropagation(); setClosing(o); }} aria-label={`${t.closeOrg}: ${o.name}`}>
                              <Ban aria-hidden="true" /> {t.closeOrg}
                            </button>
                          </>
                        ) : null}
                        {!closed && <Link className="btn btn-sm" to={href} onClick={(e) => e.stopPropagation()}>{t.open}</Link>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editing && <OrgDialog org={editing === "new" ? null : editing} msps={msps} onClose={() => setEditing(null)} />}
      {closing && <CloseDialog org={closing} onClose={() => setClosing(null)} />}
      {purging && <PurgeDialog org={purging} onClose={() => setPurging(null)} />}
    </>
  );

  async function reopen(o: Organization) {
    try {
      await api.adminReopenOrg(o.id);
      toast("ok", t.reopened(o.name));
      await qc.invalidateQueries({ queryKey: qk.orgs });
      invalidate(o.id);
    } catch (e) {
      toast("error", e instanceof ApiRequestError ? e.message : t.reopenFailed);
    }
  }
}

const PURGE_GRACE_DAYS = 30;

// Encerrar pede o nome digitado: é a operação mais destrutiva do produto, e
// um clique errado não pode custar o histórico de um cliente (ADR 0024).
function CloseDialog({ org, onClose }: { org: Organization; onClose: () => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT(organizationsMessages);
  const tc = useT(common);
  const [typed, setTyped] = useState("");
  const [purgeNow, setPurgeNow] = useState(false);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const matches = typed.trim() === org.name;

  async function confirm(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!matches) return;
    setBusy(true);
    try {
      await api.adminCloseOrg(org.id);
      if (purgeNow) await api.adminPurgeOrg(org.id);
      toast("ok", purgeNow ? t.closedAndPurging(org.name) : t.closed(org.name));
      await qc.invalidateQueries({ queryKey: qk.orgs });
      onClose();
    } catch (err) {
      toast("error", err instanceof ApiRequestError ? err.message : t.closeFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => { if (!v) onClose(); }}
      title={t.closeTitle(org.name)}
      description={t.closeExplain(org.name, PURGE_GRACE_DAYS)}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>{tc.cancel}</button>
          <button className="btn btn-primary" style={{ background: "var(--crit-mark)", borderColor: "var(--crit-mark)" }} type="submit" form="encerrar-form" disabled={busy || !matches}>
            {t.closeOrg}
          </button>
        </>
      }
    >
      <form id="encerrar-form" onSubmit={confirm} style={{ display: "grid", gap: 16 }} noValidate>
        <div className="field">
          <label htmlFor="encerrar-nome">{t.closeConfirmField}</label>
          <input id="encerrar-nome" className="input" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false}
            aria-invalid={touched && !matches} />
          {touched && !matches && <span className="error">{t.closeMismatch}</span>}
        </div>
        <label className="check" htmlFor="encerrar-apagar">
          <input id="encerrar-apagar" type="checkbox" checked={purgeNow} onChange={(e) => setPurgeNow(e.target.checked)} />
          <span><strong>{t.closePurgeNow}</strong><br /><span className="ink-2">{t.closePurgeNowHint}</span></span>
        </label>
      </form>
    </Dialog>
  );
}

function PurgeDialog({ org, onClose }: { org: Organization; onClose: () => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT(organizationsMessages);
  const tc = useT(common);
  const [busy, setBusy] = useState(false);

  async function confirm() {
    setBusy(true);
    try {
      await api.adminPurgeOrg(org.id);
      toast("ok", t.purgeRequested(org.name));
      await qc.invalidateQueries({ queryKey: qk.orgs });
      onClose();
    } catch (err) {
      toast("error", err instanceof ApiRequestError ? err.message : t.purgeFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => { if (!v) onClose(); }}
      title={t.purgeTitle(org.name)}
      description={t.purgeExplain}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>{tc.cancel}</button>
          <button className="btn btn-primary" style={{ background: "var(--crit-mark)", borderColor: "var(--crit-mark)" }} type="button" onClick={confirm} disabled={busy}>{t.purge}</button>
        </>
      }
    />
  );
}

function OrgDialog({ org, msps, onClose }: { org: Organization | null; msps: Organization[]; onClose: () => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT(organizationsMessages);
  const tc = useT(common);
  const f = useFormat();
  const [name, setName] = useState(org?.name ?? "");
  const [kind, setKind] = useState<"customer" | "msp">(org?.kind ?? "customer");
  const [parentId, setParentId] = useState(org?.parentId ?? "");
  // Quota da primeira área de armazenamento, só na criação (ADR 0037); vazio é sem limite.
  const [quotaGb, setQuotaGb] = useState("");
  // O idioma da organização nova começa no de quem está criando.
  const [locale, setLocale] = useState<Locale>(useLocale());
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);
  const nameError = touched && !name.trim() ? t.nameRequired : null;
  // Teste gratuito (ADR 0038): manter, prorrogar até uma data ou converter em cliente.
  const trialEnd = org?.trialEndsAt ? new Date(org.trialEndsAt).getTime() : null;
  const [trialAction, setTrialAction] = useState<"keep" | "extend" | "convert">("keep");
  // Um "agora" fixo enquanto o diálogo está aberto.
  const [openedAt] = useState(() => Date.now());
  const [trialUntil, setTrialUntil] = useState(() => dateInput(Math.max(trialEnd ?? 0, openedAt) + 15 * DAY));
  const trialDateError = touched && trialAction === "extend" && !trialUntil ? t.trialDateRequired : null;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!name.trim()) return;
    if (trialAction === "extend" && !trialUntil) return;
    setBusy(true);
    const gb = Math.round(Number(quotaGb) || 0);
    const storageQuotaBytes = gb > 0 ? gb * GB : null;
    try {
      if (org) {
        const trialEndsAt = trialAction === "extend" ? endOfDayISO(trialUntil) : trialAction === "convert" ? null : undefined;
        const saved = await api.adminUpdateOrg(org.id, { name: name.trim(), ...(trialEndsAt !== undefined ? { trialEndsAt } : {}) });
        toast("ok", trialAction === "extend" && saved.trialEndsAt ? t.trialExtended(saved.name, f.fullDate(saved.trialEndsAt))
          : trialAction === "convert" ? t.trialConverted(saved.name)
          : t.saved(saved.name));
      } else {
        const created = await api.adminCreateOrg({ name: name.trim(), kind, parentId, storageQuotaBytes, locale });
        toast("ok", created.poolName ? t.created(created.name, created.poolName) : t.createdNoPool(created.name));
      }
      await qc.invalidateQueries({ queryKey: qk.orgs });
      onClose();
    } catch (err) {
      toast("error", err instanceof ApiRequestError ? err.message : org ? t.saveFailed : t.createFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => { if (!v) onClose(); }}
      title={org ? t.editTitle(org.name) : t.createTitle}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>{tc.cancel}</button>
          <button className="btn btn-primary" type="submit" form="org-form" disabled={busy}>{org ? t.save : t.create}</button>
        </>
      }
    >
      <form id="org-form" onSubmit={save} style={{ display: "grid", gap: 16 }} noValidate>
        <div className="field">
          <label htmlFor="org-nome">{t.fieldName}</label>
          <input id="org-nome" className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} aria-invalid={Boolean(nameError)} />
          {nameError && <span className="error">{nameError}</span>}
        </div>

        {/* Tipo e MSP só na criação: mover um inquilino na árvore muda quem
            enxerga o quê, e isso é outra operação. */}
        {!org && (
          <>
            <div className="field">
              <span className="label" id="org-tipo">{t.fieldKind}</span>
              <div className="segmented" role="group" aria-labelledby="org-tipo">
                <button type="button" aria-pressed={kind === "customer"} onClick={() => setKind("customer")}>{t.kindCustomer}</button>
                <button type="button" aria-pressed={kind === "msp"} onClick={() => { setKind("msp"); setParentId(""); }}>{t.kindMsp}</button>
              </div>
              <span className="hint">{t.kindHint}</span>
            </div>
            {kind === "customer" && msps.length > 0 && (
              <div className="field">
                <label htmlFor="org-pai">{t.fieldParent}</label>
                <select id="org-pai" className="select" value={parentId} onChange={(e) => setParentId(e.target.value)}>
                  <option value="">{t.parentNone}</option>
                  {msps.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            )}
            <div className="field">
              <label htmlFor="org-idioma">{t.fieldLocale}</label>
              <select id="org-idioma" className="select" value={locale} onChange={(e) => setLocale(e.target.value as Locale)}>
                {LOCALES.map((l) => <option key={l} value={l}>{LOCALE_NAMES[l]}</option>)}
              </select>
            </div>
          </>
        )}

        {!org ? (
          <div className="field">
            <label htmlFor="org-quota">{t.fieldQuota}</label>
            <input id="org-quota" className="input num" type="number" min={1} value={quotaGb} onChange={(e) => setQuotaGb(e.target.value)} />
            <span className="hint">{t.quotaHint}</span>
          </div>
        ) : (
          <p className="hint">{org.quotaBytes === null ? t.quotaAssignedNoLimit : t.quotaAssigned(f.gb(org.quotaBytes))}</p>
        )}

        {org?.trialEndsAt && trialEnd !== null && (
          <fieldset className="field" style={{ border: 0, padding: 0, margin: 0, display: "grid", gap: 10 }}>
            <legend className="label" style={{ marginBottom: 6 }}>{t.trialSection}</legend>
            <span className="hint">{trialEnd <= openedAt ? t.trialStatusEnded(f.fullDate(org.trialEndsAt)) : t.trialStatus(f.fullDate(org.trialEndsAt))}</span>
            <label className="check" htmlFor="org-trial-keep">
              <input id="org-trial-keep" type="radio" name="org-trial" checked={trialAction === "keep"} onChange={() => setTrialAction("keep")} />
              <span>{t.trialKeep}</span>
            </label>
            <label className="check" htmlFor="org-trial-extend">
              <input id="org-trial-extend" type="radio" name="org-trial" checked={trialAction === "extend"} onChange={() => setTrialAction("extend")} />
              <span>{t.trialExtend}</span>
            </label>
            {trialAction === "extend" && (
              <div style={{ display: "grid", gap: 6, paddingLeft: 26 }}>
                <label className="sr-only" htmlFor="org-trial-until">{t.trialExtendDate}</label>
                <input id="org-trial-until" className="input" type="date" min={dateInput(openedAt)} value={trialUntil}
                  onChange={(e) => setTrialUntil(e.target.value)} aria-invalid={Boolean(trialDateError)} style={{ maxWidth: 200 }} />
                {trialDateError && <span className="error">{trialDateError}</span>}
              </div>
            )}
            <label className="check" htmlFor="org-trial-convert">
              <input id="org-trial-convert" type="radio" name="org-trial" checked={trialAction === "convert"} onChange={() => setTrialAction("convert")} />
              <span>{t.trialConvert}<br /><span className="ink-2">{t.trialConvertHint}</span></span>
            </label>
          </fieldset>
        )}
      </form>
    </Dialog>
  );
}
